import { cn } from "@lib/cn";
import { IconDevices } from "@tabler/icons-react";
import ReactECharts from "echarts-for-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ANALYTICS_QUERY,
  PIE_CHART_DEVICE_MAP,
  PIE_CHART_OPTIONS,
} from "../../constants";
import { useAnalyticsQuery } from "../../hooks";
import { EmptyStateMessage } from "../empty-state-message";

import { useIntersectionObserver } from "usehooks-ts";
import { ErrorStateMessage } from "../error-state-message";
import { CustomLegendItem } from "./custom-legend-item";
import { Skeleton } from "./skeleton";

type DeviceData = {
  device: string;
  sessions: number;
};

type ChartDeviceData = DeviceData & {
  name: string;
  value: number;
  itemStyle: { color: string };
  icon: React.ElementType;
};

export const Devices = ({
  overrides,
  className,
}: {
  overrides?: Record<string, unknown> | undefined;
  /** Merged into the card's root. The default `-2xl:tw-col-span-2` is right for
   *  the three-column row this card was built for, and wrong in a two-column
   *  one - where it makes the card take the whole row and orphans its
   *  neighbour. Pass `-2xl:tw-col-span-1` there. */
  className?: string;
}) => {
  const chartRef = useRef<ReactECharts>(null);
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.1,
  });
  const { data, isLoading, isError } = useAnalyticsQuery({
    enabled: isIntersecting,
    query: [ANALYTICS_QUERY.DEVICES],
    overrides: overrides ?? {},
  });
  const deviceData = useMemo(
    () => (data?.[ANALYTICS_QUERY.DEVICES] as DeviceData[]) ?? [],
    [data],
  );

  const totalSessions = useMemo(() => {
    return deviceData.reduce(
      (acc, item) => acc + (Number(item.sessions) || 0),
      0,
    );
  }, [deviceData]);

  const chartData: ChartDeviceData[] = useMemo(() => {
    if (!deviceData.length || !totalSessions) return [];
    return deviceData.map((item) => ({
      ...item,
      name: item.device,
      value: Math.round(((Number(item.sessions) || 0) / totalSessions) * 100),
      ...(PIE_CHART_DEVICE_MAP[
        item.device as keyof typeof PIE_CHART_DEVICE_MAP
      ] ?? {
        itemStyle: { color: "#CBD5E1" },
        icon: IconDevices,
      }),
    }));
  }, [deviceData, totalSessions]);

  const handleLegendClick = useCallback((name: string) => {
    const chartInstance = chartRef.current?.getEchartsInstance();
    if (!chartInstance) return;
    chartInstance.dispatchAction({
      type: "legendToggleSelect",
      name,
    });
    setHiddenSeries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(name)) newSet.delete(name);
      else newSet.add(name);
      return newSet;
    });
  }, []);

  // Drop "hidden" entries that no longer exist in the current dataset.
  useEffect(() => {
    const availableNames = new Set(chartData.map((d) => d.device));
    setHiddenSeries((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set<string>();
      prev.forEach((name) => {
        if (availableNames.has(name)) next.add(name);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [chartData]);

  const option = useMemo(
    () => ({
      ...PIE_CHART_OPTIONS,
      series: [{ ...PIE_CHART_OPTIONS.series[0], data: chartData }],
    }),
    [chartData],
  );

  const hasData = totalSessions > 0;

  let content: React.ReactNode;
  if (isLoading)
    content = (
      <div className="tw-relative tw-size-full">
        <Skeleton />
      </div>
    );
  else if (isError) content = <ErrorStateMessage />;
  else if (!hasData)
    content = (
      <EmptyStateMessage
        title="No analytics yet, but you're on the right track"
        description="Share your Storydocs to start collecting insights. We’ll show your data as soon as it's ready."
      />
    );
  else
    content = (
      <div className="tw-relative -tw-ml-2 tw-flex tw-flex-1 tw-items-center tw-justify-center tw-gap-4 -sm:tw-gap-2">
        {/* Donut Chart */}
        <div className="tw-flex tw-size-[140px] tw-shrink-0 tw-items-center tw-justify-center">
          <ReactECharts
            ref={chartRef}
            option={option}
            style={{ height: "100%", width: "100%" }}
            opts={{ renderer: "svg" }}
          />
        </div>
        {/* Legend */}
        <div className="tw-flex tw-flex-col tw-justify-between tw-gap-4">
          {chartData.map((item) => (
            <CustomLegendItem
              key={item.device}
              device={item.device}
              color={item.itemStyle.color}
              Icon={item.icon}
              percentage={item.value}
              isHidden={hiddenSeries.has(item.device)}
              onToggle={handleLegendClick}
            />
          ))}
        </div>
      </div>
    );

  return (
    <div
      ref={ref}
      className={cn(
        "tw-flex tw-min-h-[264px] tw-flex-col tw-gap-4 tw-rounded-xl tw-bg-white tw-p-5 tw-shadow-2xs tw-outline tw-outline-1 tw-outline-blue/5 -2xl:tw-col-span-2 -md:tw-col-span-1",
        className,
      )}
    >
      {/* Header */}
      <div className="tw-flex tw-items-center tw-gap-2">
        <IconDevices className="tw-size-4" />
        <h2 className="tw-font-sans tw-text-base tw-font-bold tw-text-blue">
          Devices
        </h2>
      </div>

      {/* Content */}
      {content}
    </div>
  );
};
